import React, { memo } from "react";
import { styled } from "@mui/material/styles";
import {
    FormControl, IconButton, InputLabel, OutlinedInput,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useStore from "../../store";
import PTMTooltip from "../PTMToolTip";

const StyledFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => !["darkMode", "label"].includes(prop),
})(({
    theme, darkMode, label,
}) => ({
    // [`& .${classes.base}`]: {
    "& .Mui-disabled fieldset": {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    "& .Mui-disabled": {
        color: `${theme.palette.text.primary}!important`,
        WebkitTextFillColor: "initial",
    },
    "&>.MuiGrid-container": {
        marginTop: 10,
        marginBottom: -4,
        "& .MuiGrid-item": {
            paddingTop: 4,
        },
    },
    "& .MuiOutlinedInput-root": {
        background: "none",
        fontSize: 12,
        margin: 0,
        padding: 0,
        colorScheme: darkMode ? "dark" : "light",
        "& .MuiOutlinedInput-notchedOutline": {
            "&  legend": {
                border: "none",
            },
            top: 0,
            height: 24,
            ...label && {
                marginTop: 10,
            },
        },
        "& .MuiOutlinedInput-input": {
            paddingTop: 4,
            paddingBottom: 2,
            paddingLeft: 3,
            paddingRight: 3,
        },
        "& .MuiIconButton-root": {
            minWidth: 16,
            marginRight: 4,
        },
        ...label ? {
            marginTop: 4,
            "&>*:not(.MuiOutlinedInput-notchedOutline)": {
                marginTop: 12,
            },
            // fix for PTMInput-switch endAdornment
            "& .PTMInput-switch": {
                // background: "orange",
                width: "20%",
                paddingTop: "4px",
                marginTop: -2,
                "& .MuiGrid-container": {
                    marginTop: 12,
                },
            },
            "& .MuiIconButton-root": {
                height: 21,
            },
        } : {
            "& .MuiSelect-iconOutlined": {
                marginTop: "1px!important",
            },
            "&>*:not(.MuiOutlinedInput-notchedOutline):not(.MuiSelect-select)": {
                marginTop: 2,
            },
            "& .MuiSelect-select": {
                marginTop: 0.75,
            },
        },
    },
    "& .MuiInputLabel-shrink": {
        fontSize: 12,
        transform: "translate(2px, -7px) scale(1) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        display: "none",
    },
    // fix for PTMInput-switch endAdornment
    "& .MuiInputBase-adornedEnd>.PTMInput-switch": {
        marginTop: "-5px!important",
    },
    // },
    ...label ? {
        height: 36,
    } : {
        height: 24,
    },

}));

const CustomTextField = React.memo((props) => {
    const {
        fullWidth,
        disabled,
        required,
        className,
        label,
        name,
        placeholder,
        value,
        onChange,
        onBlur,
        onClick,
        startAdornment,
        endAdornment,
        sx,
        inputRef,
        error,
        rows,
        inputProps,
        autoFocus,
        allowCopy,
        allowNegative = false,
        thousandSeparator = ".",
        decimalSeparator = ",",
        decimalScale = 2,
        fixedDecimalScale = true,
    } = props;
    const darkMode = useStore((store) => store.app.darkMode);

    const [toolTipText, setToolTipText] = React.useState("Inhalt kopieren");

    const copyToClipboard = (e, val) => {
        e.preventDefault();
        e.stopPropagation();

        setToolTipText("Kopiert");

        const clippableTitle = val;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(clippableTitle);
            // If website is not secure run this code
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = clippableTitle;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            return new Promise((res, rej) => {
                /* eslint-disable-next-line no-unused-expressions */
                document.execCommand("copy") ? res() : rej();
                textArea.remove();
            });
        }
        setTimeout(() => {
            setToolTipText("Inhalt kopieren");
        }, 1000);
    };

    return (
        <StyledFormControl
            fullWidth={fullWidth}
            disabled={disabled}
            error={error}
            required={required}
            sx={sx}
            label={label}
            darkMode={darkMode}
        >
            {label && (
                <InputLabel>
                    {label}
                </InputLabel>
            )}
            <NumericFormat
                inputRef={inputRef}
                value={value}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
                onValueChange={onChange}
                customInput={OutlinedInput}
                className={className}
                allowNegative={allowNegative}
                sx={{
                    "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                }}
                onClick={onClick}
                fullWidth={fullWidth}
                label={label}
                name={name}
                placeholder={placeholder}
                onBlur={onBlur}
                autoFocus={autoFocus}
                startAdornment={startAdornment}
                endAdornment={(
                    <>
                        {allowCopy && (
                            <IconButton onClick={(e) => copyToClipboard(e, value)}>
                                <PTMTooltip title={toolTipText}>
                                    <FontAwesomeIcon icon="copy" />
                                </PTMTooltip>
                            </IconButton>
                        )}
                        {endAdornment}
                    </>
                )}
                rows={rows}
                inputProps={{
                    ...inputProps,
                    max: inputProps?.max ? inputProps.max : "9999-12-31",
                }}
            />
        </StyledFormControl>
    );
});

export default memo(CustomTextField);
